/* God hand ("Gotteshand"), client side: tool button, pointer/touch/wheel input, voxel hand cursor, ground shadows and
   the landing / wall / pick-up effects. The simulation part is src/sim/features/grab.js; everything that changes the
   battle is a feature command (ctx.send, see featCmd in sim.worker.js: single player queue, replays and lockstep), with
   positions and velocities in 1/16 units.
   Mouse: press on a unit = grab (Alt = a bundle in a radius), drag = carry, release = throw with the cursor's velocity
   over the last ~100 ms. Wheel or Shift + vertical drag = height. Touch: hold a unit briefly to grab, swipe to throw. */
import './grab.css';

const EV_GRAB_LAND = 40, EV_GRAB_WALL = 41, EV_GRAB_PICK = 42;
const HMAX = 32, THROW_WIN = 110, VMAX = 45, HOLD_MS = 220;
const clamp = (v, a, b) => v < a ? a : v > b ? b : v;

/**
 * ctx: { THREE, scene, canvas, camera, send(command), touches, groundH, pickUnit, setTool, toast, shake, shakeNear,
 *        st: () => ({ tool, possessed, simState, paused, curB, prevB, tCur, frameMs, N, split, armies, gen, camDist, fx, debris, locked }) }
 */
export function createGrabHand(ctx) {
  const { THREE, scene, canvas, camera } = ctx;
  const COARSE = matchMedia('(pointer: coarse)').matches;

  /* ---- input state ---- */
  let held = [], gen = -1, pid = -1, lift = 4, planeY = 0, inside = false, alt = false, shiftY = null;
  let sx = 0, sy = 0, lastSend = 0, sentX = NaN, sentY = NaN, sentZ = NaN, cand = null;
  const tgt = { x: 0, y: 0, z: 0 }, hov = { cx: 0, cy: 0, ok: false };
  const SMP = new Float64Array(16 * 4); let smpN = 0, smpHead = 0;   // ring: t, x, y, z
  const flying = [];                                                  // thrown units, for their shadows
  const ray = new THREE.Raycaster(), ndc = new THREE.Vector2(), plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), pt = new THREE.Vector3();

  const q16 = v => Math.round(v * 16);
  const send = c => {
    const o = { t: c.t };
    if (c.ids) o.ids = c.ids;
    for (const k of ['x', 'y', 'z', 'vx', 'vy', 'vz']) if (c[k] !== undefined) o[k] = q16(c[k]);
    ctx.send(o);
  };
  const usable = S => S.tool === 'grab' && S.possessed < 0 && S.simState === 'running' && !S.locked;
  const armyR = (S, i) => (S.armies[S.GRP && S.GRP.length > i ? S.GRP[i] : i < S.split ? 0 : 1] || S.armies[0]).stats; // the unit's group

  // cursor ray against the horizontal plane at height y (null when looking above it)
  function rayPlane(cx, cy, y, out) {
    const r = canvas.getBoundingClientRect();
    ndc.set(((cx - r.left) / r.width) * 2 - 1, -((cy - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera); plane.constant = -y;
    return ray.ray.intersectPlane(plane, out);
  }
  function groundPoint(cx, cy, out) {
    // two passes: flat guess, then the plane at the terrain height found there (good enough for a cursor)
    if (!rayPlane(cx, cy, 0, out)) return null;
    const g = ctx.groundH(out.x, out.z);
    return g ? rayPlane(cx, cy, g, out) : out;
  }

  /* ---- grab / hold / throw ---- */
  function bundleAt(S, x, z, R) {
    const b = S.curB, list = [];
    for (let i = 0; i < S.N; i++) {
      const o = i * 10; if (!b[o + 9]) continue;
      const dx = b[o] - x, dz = b[o + 2] - z, d = dx * dx + dz * dz, rr = R + armyR(S, i).r;
      if (d < rr * rr) list.push(d, i);
    }
    const idx = []; for (let k = 0; k < list.length; k += 2) idx.push(k);
    idx.sort((a, c) => list[a] - list[c]);
    return idx.slice(0, HMAX).map(k => list[k + 1]);
  }
  const bundleR = S => clamp(3 + S.camDist * 0.03, 3, 14);

  function begin(S, ids, cx, cy) {
    const b = S.curB; let x = 0, z = 0, h = 0;
    for (const i of ids) { x += b[i * 10]; z += b[i * 10 + 2]; h = Math.max(h, armyR(S, i).h); }
    x /= ids.length; z /= ids.length;
    held = ids; gen = S.gen; sx = cx; sy = cy; shiftY = null; smpN = 0;
    lift = clamp(2.5 + h * 0.7, 2.5, 40);
    planeY = ctx.groundH(x, z) + lift;
    if (!rayPlane(cx, cy, planeY, pt)) pt.set(x, planeY, z);
    tgt.x = pt.x; tgt.y = planeY; tgt.z = pt.z;
    send({ t: 'grab', ids, x: tgt.x, y: tgt.y, z: tgt.z });
    sentX = tgt.x; sentY = tgt.y; sentZ = tgt.z; lastSend = performance.now();
    sample(lastSend);
    if (navigator.vibrate && COARSE) navigator.vibrate(12);
  }
  function aim(cx, cy, dy) {
    if (shiftY !== null) { lift = clamp(lift - dy * 0.004 * Math.max(10, ctx.st().camDist), 0.8, 60); }
    else { sx = cx; sy = cy; }
    planeY = ctx.groundH(tgt.x, tgt.z) + lift;
    if (rayPlane(sx, sy, planeY, pt)) { tgt.x = pt.x; tgt.z = pt.z; }
    tgt.y = planeY;
  }
  function sample(now) {
    const o = smpHead * 4; SMP[o] = now; SMP[o + 1] = tgt.x; SMP[o + 2] = tgt.y; SMP[o + 3] = tgt.z;
    smpHead = (smpHead + 1) % 16; if (smpN < 16) smpN++;
  }
  // smoothed release velocity: average over the samples of the last THROW_WIN ms (0 when the hand stood still)
  function throwVel(now, out) {
    out.x = out.y = out.z = 0;
    const last = ((smpHead + 15) % 16) * 4;
    if (!smpN || now - SMP[last] > THROW_WIN) return out;
    let first = last;
    for (let k = 1; k < smpN; k++) { const o = ((smpHead + 15 - k) % 16) * 4; if (now - SMP[o] > THROW_WIN) break; first = o; }
    const dt = (SMP[last] - SMP[first]) / 1000; if (dt < 0.025) return out;
    out.x = (SMP[last + 1] - SMP[first + 1]) / dt; out.y = (SMP[last + 2] - SMP[first + 2]) / dt; out.z = (SMP[last + 3] - SMP[first + 3]) / dt;
    const sp = Math.hypot(out.x, out.y, out.z); if (sp > VMAX) { const q = VMAX / sp; out.x *= q; out.y *= q; out.z *= q; }
    out.y += Math.min(6, Math.hypot(out.x, out.z) * 0.12); // a flick arcs a little
    return out;
  }
  const VEL = { x: 0, y: 0, z: 0 };
  function release(fling) {
    if (!held.length) return;
    const now = performance.now();
    if (fling) throwVel(now, VEL); else VEL.x = VEL.y = VEL.z = 0;
    send({ t: 'grabThrow', vx: VEL.x, vy: VEL.y, vz: VEL.z });
    for (const i of held) flying.push(i, now);
    held = []; pid = -1; shiftY = null; openT = now;
  }

  /* ---- toolbar button + key H ---- */
  const tb = document.querySelector('#tools [data-tool="hand"]');
  if (tb) {
    const b = document.createElement('button');
    b.dataset.tool = 'grab'; b.setAttribute('aria-pressed', 'false'); b.className = 'grab-tool';
    b.title = 'Einheiten packen und werfen. Alt: Pulk packen. Mausrad oder Shift: Höhe.';
    b.innerHTML = '<b>H</b>Gotteshand';
    tb.after(b);
    b.addEventListener('click', hint);
  }
  let hinted = false;
  function hint() {
    if (hinted) return; hinted = true;
    ctx.toast(COARSE ? 'Gotteshand: Einheit kurz halten zum Packen, wischen zum Werfen' : 'Gotteshand: Einheit packen, ziehen und loslassen zum Werfen. Alt packt einen Pulk, Mausrad oder Shift ändert die Höhe.');
  }
  addEventListener('keydown', e => {
    if (e.target.matches && e.target.matches('input,select,textarea')) return;
    if (e.key === 'Alt') alt = true;
    if (e.code === 'KeyH' && !e.repeat) { const S = ctx.st(); if (S.possessed < 0 && S.simState !== 'setup') { ctx.setTool('grab'); hint(); } }
  });
  addEventListener('keyup', e => { if (e.key === 'Alt') alt = false; });
  addEventListener('blur', () => { alt = false; });

  /* ---- pointer input (registered before the game's own handlers, so a grab can swallow the event) ---- */
  canvas.addEventListener('pointerdown', e => {
    const S = ctx.st();
    if (!usable(S) || S.paused || !S.curB || held.length) return;
    if (e.pointerType === 'touch') {
      // hold to grab: only a single finger resting on a unit
      if (ctx.touches.size) { cand = null; return; }
      const u = ctx.pickUnit(e.clientX, e.clientY);
      if (u >= 0) cand = { id: e.pointerId, u, x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, timer: setTimeout(touchGrab, HOLD_MS) };
      return;
    }
    if (e.button !== 0) return;
    let ids;
    if (e.altKey || alt) { if (!groundPoint(e.clientX, e.clientY, pt)) return; ids = bundleAt(S, pt.x, pt.z, bundleR(S)); }
    else { const u = ctx.pickUnit(e.clientX, e.clientY); ids = u >= 0 ? [u] : []; }
    if (!ids.length) return;
    e.stopImmediatePropagation(); canvas.focus(); canvas.setPointerCapture(e.pointerId);
    pid = e.pointerId; begin(S, ids, e.clientX, e.clientY);
  });
  function touchGrab() {
    const c = cand; cand = null; if (!c) return;
    const S = ctx.st();
    if (!usable(S) || S.paused || held.length || ctx.touches.size !== 1 || !ctx.touches.has(c.id) || !S.curB[c.u * 10 + 9]) return;
    ctx.touches.delete(c.id); // the game stops orbiting the camera with this finger
    pid = c.id; begin(S, [c.u], c.x, c.y);
  }
  canvas.addEventListener('pointermove', e => {
    if (cand && e.pointerId === cand.id) { cand.x = e.clientX; cand.y = e.clientY; if (Math.hypot(cand.x - cand.sx, cand.y - cand.sy) > 12) { clearTimeout(cand.timer); cand = null; } }
    if (held.length && e.pointerId === pid) {
      if (e.shiftKey && shiftY === null) shiftY = e.clientY; else if (!e.shiftKey) shiftY = null;
      aim(e.clientX, e.clientY, e.movementY || 0);
      sample(performance.now());
    } else if (e.pointerType !== 'touch') { hov.cx = e.clientX; hov.cy = e.clientY; hov.ok = true; inside = true; }
  });
  const up = e => {
    if (cand && e.pointerId === cand.id) { clearTimeout(cand.timer); cand = null; }
    if (held.length && e.pointerId === pid) release(e.type === 'pointerup');
  };
  canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);
  canvas.addEventListener('pointerenter', () => { inside = true; }); canvas.addEventListener('pointerleave', () => { inside = false; });
  canvas.addEventListener('wheel', e => {
    if (!held.length) return;
    e.preventDefault(); e.stopImmediatePropagation();
    lift = clamp(lift * Math.exp(-e.deltaY * 0.0015), 0.8, 60); aim(sx, sy, 0); sample(performance.now());
  }, { passive: false });

  /* ---- visuals: voxel hand, blob shadows, bundle ring, craters ---- */
  const skin = new THREE.MeshLambertMaterial({ color: 0xf1c7a0, emissive: 0x3a2412 });
  const gold = new THREE.MeshLambertMaterial({ color: 0xffd25a, emissive: 0x6a4a08 });
  const cube = new THREE.BoxGeometry(1, 1, 1);
  const box = (mat, w, h, d, x, y, z, parent) => { const m = new THREE.Mesh(cube, mat); m.scale.set(w, h, d); m.position.set(x, y, z); parent.add(m); return m; };
  const hand = new THREE.Group(); hand.visible = false; hand.rotation.order = 'YXZ';
  box(skin, 1, 0.34, 1, 0, 0, 0, hand);                      // palm (fingers point to +z, palm faces down)
  box(gold, 1.12, 0.5, 0.36, 0, 0.02, -0.66, hand);           // cuff
  box(skin, 0.9, 0.42, 0.3, 0, 0, -0.98, hand);               // wrist
  const fingers = [];
  [[-0.375, 0.4], [-0.125, 0.48], [0.125, 0.45], [0.375, 0.34]].forEach(([x, L]) => {
    const p1 = new THREE.Group(); p1.position.set(x, 0, 0.5); hand.add(p1);
    box(skin, 0.21, 0.24, L, 0, 0, L / 2, p1);
    const p2 = new THREE.Group(); p2.position.set(0, 0, L); p1.add(p2);
    box(skin, 0.2, 0.22, L * 0.8, 0, 0, L * 0.4, p2);
    fingers.push(p1, p2);
  });
  const thumb = new THREE.Group(); thumb.position.set(-0.52, -0.02, 0.05); thumb.rotation.y = -0.7; hand.add(thumb);
  box(skin, 0.24, 0.26, 0.5, 0, 0, 0.25, thumb);
  hand.traverse(o => { if (o.isMesh) o.castShadow = true; });
  scene.add(hand);

  const SH = 64;
  const shGeo = new THREE.CircleGeometry(1, 24); shGeo.rotateX(-Math.PI / 2);
  const shMat = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.3, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const shadows = new THREE.InstancedMesh(shGeo, shMat, SH); shadows.count = 0; shadows.frustumCulled = false; shadows.renderOrder = 2;
  scene.add(shadows);
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.94, 1, 48).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xffd25a, transparent: true, opacity: 0.8, depthWrite: false }));
  ring.visible = false; scene.add(ring);
  const craters = [];
  const crGeo = new THREE.CircleGeometry(1, 28).rotateX(-Math.PI / 2);
  for (let k = 0; k < 8; k++) {
    const m = new THREE.Mesh(crGeo, new THREE.MeshBasicMaterial({ color: 0x3b2a18, transparent: true, opacity: 0, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 }));
    m.visible = false; m.renderOrder = 1; scene.add(m); craters.push({ m, t: 0 });
  }
  let crNext = 0;

  const M4 = new THREE.Matrix4(), Q0 = new THREE.Quaternion(), V0 = new THREE.Vector3(), V1 = new THREE.Vector3();
  let closeK = 0, openT = 0, shN = 0;
  function addShadow(x, y, z, r) {
    if (shN >= SH) return;
    const g = ctx.groundH(x, z), h = Math.max(0, y - g), s = r * 1.4 / (1 + h * 0.035);
    M4.compose(V0.set(x, g + 0.06, z), Q0, V1.set(s, 1, s)); shadows.setMatrixAt(shN++, M4);
  }
  const P = { x: 0, y: 0, z: 0 };
  function unitPos(S, i, alpha, out) {
    const b = S.curB, pb = S.prevB && S.prevB.length === b.length ? S.prevB : b, o = i * 10;
    out.x = pb[o] + (b[o] - pb[o]) * alpha; out.y = pb[o + 1] + (b[o + 1] - pb[o + 1]) * alpha; out.z = pb[o + 2] + (b[o + 2] - pb[o + 2]) * alpha;
    return b[o + 9];
  }

  /** per render frame (after the units were placed) */
  function frame(dt, now) {
    const S = ctx.st();
    if (held.length && S.gen !== gen) { held = []; pid = -1; }      // new battle: the worker forgot everything
    if (S.gen !== gen) flying.length = 0;
    if (held.length && !usable(S)) release(false);
    const on = S.tool === 'grab' && S.possessed < 0 && S.simState !== 'setup';
    canvas.classList.toggle('grab-on', on && inside && !COARSE);
    shN = 0;
    const alpha = S.curB ? clamp((now - S.tCur) / S.frameMs, 0, 1) : 0;

    if (held.length) {
      // keep the hold point on the cursor (the camera may move) and stream it to the sim at ~30 Hz
      aim(sx, sy, 0);
      if (!S.paused && now - lastSend > 33 && Math.abs(tgt.x - sentX) + Math.abs(tgt.y - sentY) + Math.abs(tgt.z - sentZ) > 0.06) {
        send({ t: 'grabHold', x: tgt.x, y: tgt.y, z: tgt.z }); sentX = tgt.x; sentY = tgt.y; sentZ = tgt.z; lastSend = now;
      }
      if (now - SMP[((smpHead + 15) % 16) * 4] > 30) sample(now);
    }

    // hand: over the held units (pulled a bit towards the cursor), else under the cursor
    let hx = 0, hy = 0, hz = 0, hs = clamp(S.camDist * 0.022, 0.7, 7), show = false;
    if (held.length && S.curB) {
      let n = 0, top = 0, r = 0;
      for (const i of held) {
        if (!unitPos(S, i, alpha, P)) continue;
        const A = armyR(S, i); hx += P.x; hy += P.y; hz += P.z; top = Math.max(top, P.y + A.h); r = Math.max(r, A.r); n++;
        addShadow(P.x, P.y, P.z, A.r);
      }
      if (n) {
        hx = hx / n * 0.7 + tgt.x * 0.3; hz = hz / n * 0.7 + tgt.z * 0.3;
        hs = Math.max(hs * 0.8, r * 1.3); hy = top + hs * 0.25; show = true;
      } else { held = []; pid = -1; } // all of them died in the hand
      closeK += (1 - closeK) * Math.min(1, dt * 14);
    } else {
      closeK += (0 - closeK) * Math.min(1, dt * (now - openT < 300 ? 22 : 10));
      if (on && inside && !COARSE && hov.ok && groundPoint(hov.cx, hov.cy, pt)) {
        hx = pt.x; hy = pt.y + hs * 0.6; hz = pt.z; show = true;
        addShadow(hx, hy, hz, hs * 0.45);
        if (alt && S.simState === 'running') { const R = bundleR(S); ring.visible = true; ring.position.set(pt.x, pt.y + 0.08, pt.z); ring.scale.setScalar(R); }
      }
    }
    if (held.length || !alt || !show) ring.visible = false;
    hand.visible = show;
    if (show) {
      const yaw = Math.atan2(hx - camera.position.x, hz - camera.position.z);
      hand.rotation.set(0.35 + closeK * 0.25, yaw, Math.sin(now * 0.004) * 0.06);
      hand.scale.setScalar(hs);
      // fingertips over the point: shift the hand back along its own forward axis
      const back = hs * (0.9 - closeK * 0.35);
      hand.position.set(hx - Math.sin(yaw) * back, hy, hz - Math.cos(yaw) * back);
      for (let k = 0; k < fingers.length; k += 2) { fingers[k].rotation.x = 0.15 + closeK * 1.25; fingers[k + 1].rotation.x = 0.1 + closeK * 1.15; }
      thumb.rotation.z = -closeK * 0.9; thumb.rotation.x = closeK * 0.6;
    }

    // thrown units keep a shadow until they are down again
    if (S.curB) {
      let m = 0;
      for (let k = 0; k < flying.length; k += 2) {
        const i = flying[k], t0 = flying[k + 1];
        const st = unitPos(S, i, alpha, P);
        if (!st || now - t0 > 6000 || (st < 1.5 && now - t0 > 150)) continue;
        flying[m++] = i; flying[m++] = t0;
        addShadow(P.x, P.y, P.z, armyR(S, i).r);
      }
      flying.length = m;
    }
    shadows.count = shN; if (shN) shadows.instanceMatrix.needsUpdate = true;

    for (const c of craters) {
      if (!c.m.visible) continue;
      const a = (now - c.t) / 14000;
      if (a >= 1) { c.m.visible = false; continue; }
      c.m.material.opacity = 0.5 * (1 - a * a);
    }
  }

  /* ---- sim events -> effects ---- */
  function events(ev, n) {
    if (!n) return;
    let any = false;
    for (let k = 0; k < n; k++) {
      const o = k * 8, type = ev[o];
      if (type < EV_GRAB_LAND || type > EV_GRAB_PICK) continue;
      const S = ctx.st(), fx = S.fx, debris = S.debris;
      const x = ev[o + 1], y = ev[o + 2], z = ev[o + 3];
      any = true;
      if (type === EV_GRAB_LAND) {
        const R = ev[o + 4], sm = ev[o + 5], sp = ev[o + 6], heavy = ev[o + 7] > 0, g = ctx.groundH(x, z);
        const nd = Math.min(48, 14 + R * 3) | 0;
        for (let i = 0; i < nd; i++) {
          const a = (i / nd) * 6.28, v = R * (1.1 + Math.random() * 0.7);
          fx.spawn(x, g + 0.3, z, Math.sin(a) * v, 0.8 + Math.random() * 1.6, Math.cos(a) * v, 0.6 + Math.random() * 0.5, 0.3 + R * 0.05 + sm * 0.05, 0, 3, 2, 0.86, 0.78, 0.6);
        }
        const nc = heavy ? 30 : 8;
        for (let i = 0; i < nc; i++) {
          const a = Math.random() * 6.28, v = 2 + Math.random() * (heavy ? 7 : 4);
          debris.spawn(x + Math.cos(a) * R * 0.3, g + 0.2, z + Math.sin(a) * R * 0.3, Math.cos(a) * v, 4 + Math.random() * (heavy ? 9 : 5), Math.sin(a) * v, 8, 0.14 + Math.random() * (heavy ? 0.35 : 0.15), 18, 6, 0, 0.38, 0.29, 0.18, g);
        }
        if (heavy || sp > 22) {
          const c = craters[crNext]; crNext = (crNext + 1) % craters.length;
          c.m.position.set(x, g + 0.04, z); c.m.scale.setScalar(R * (heavy ? 0.6 : 0.35)); c.m.material.opacity = 0.5; c.m.visible = true; c.t = performance.now();
        }
        if (heavy) {
          for (let i = 0; i < 14; i++) { const a = Math.random() * 6.28, v = Math.random() * 1.5; fx.spawn(x + Math.cos(a) * R * 0.3, g + 0.5, z + Math.sin(a) * R * 0.3, Math.cos(a) * v, 2 + Math.random() * 3, Math.sin(a) * v, 1.4 + Math.random() * 0.8, R * (0.25 + Math.random() * 0.2), 0, 1.2, 2, 0.8, 0.72, 0.58); }
          ctx.shake(R * 1.3, x, z);
        } else ctx.shakeNear(R * 0.5, x, z);
      } else if (type === EV_GRAB_WALL) {
        const sp = ev[o + 4], sm = ev[o + 5];
        for (let i = 0; i < 10 + sm * 2; i++) {
          const a = Math.random() * 6.28, v = 2 + Math.random() * 4;
          debris.spawn(x, y, z, Math.cos(a) * v, 2 + Math.random() * 5, Math.sin(a) * v, 8, 0.12 + Math.random() * 0.2, 18, 7, 0, 0.6, 0.58, 0.54, ctx.groundH(x, z));
        }
        for (let i = 0; i < 8; i++) { const a = Math.random() * 6.28; fx.spawn(x, y, z, Math.cos(a) * 1.5, 0.5 + Math.random(), Math.sin(a) * 1.5, 0.7 + Math.random() * 0.4, 0.5 + sm * 0.1, 0, 2, 2, 0.8, 0.76, 0.7); }
        ctx.shakeNear(Math.min(4, sp * 0.12 * sm), x, z);
      } else {
        const r = ev[o + 4], g = ctx.groundH(x, z);
        for (let i = 0; i < 6; i++) { const a = Math.random() * 6.28, v = 0.6 + Math.random() * r; fx.spawn(x + Math.cos(a) * r, g + 0.2, z + Math.sin(a) * r, Math.cos(a) * v, 0.6 + Math.random() * 0.8, Math.sin(a) * v, 0.7 + Math.random() * 0.4, 0.3 + r * 0.3, 0, 1.5, 2, 0.86, 0.8, 0.68); }
      }
    }
    if (any) { const S = ctx.st(); S.fx.flush(); S.debris.flush(); }
  }

  return { frame, events, holding: () => held.length > 0 };
}
